import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Spinner } from '../../components/common/Spinner';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../context/AuthContext';
import { Plus, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const KNOWN_KEYS = [
  { key: 'platform_fee', label: 'Platform Fee (%)', description: 'Fee charged on each draw' },
  { key: 'affiliate_percentage', label: 'Affiliate Commission (%)', description: 'Commission for referrals' },
  { key: 'affiliate_duration_days', label: 'Affiliate Duration (days)', description: 'How long referral commissions last' },
  { key: 'affiliate_commission_cap', label: 'Affiliate Commission Cap', description: '0 = unlimited' },
  { key: 'agent_commission_percentage', label: 'Agent Commission (%)', description: 'Commission agents earn per ticket sale (default: 10)' },
  { key: 'agent_large_win_threshold', label: 'Agent Large Win Threshold', description: 'Wins above this amount require a super agent to payout (0 = disabled)' },
  { key: 'withdrawal_approval_threshold', label: 'Withdrawal Approval Threshold', description: 'Withdrawals above this amount require admin approval before sending to Paystack' },
  { key: 'min_withdrawal', label: 'Minimum Withdrawal', description: 'Minimum amount a user can withdraw' },
];

export const AdminConfig: React.FC = () => {
  const { user } = useAuth();
  const isAuditor = user?.role === 'AUDITOR';
  const queryClient = useQueryClient();
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'config'],
    queryFn: () => adminService.getConfig(),
  });

  const [savingKey, setSavingKey] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => adminService.updateConfig(key, value),
    onSuccess: () => {
      setSavingKey(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
      toast.success('Config updated');
    },
    onError: () => toast.error('Failed to update config'),
  });

  if (isLoading) {
    return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  }

  const configs = data?.data || [];
  const configMap = Object.fromEntries(configs.map((c) => [c.key, c.value]));

  const handleSave = (key: string) => {
    const value = editValues[key] ?? configMap[key] ?? '';
    setSavingKey(key);
    updateMutation.mutate({ key, value });
  };

  const handleAddNew = () => {
    if (!newKey.trim() || !newValue.trim()) return;
    setSavingKey('__new__');
    updateMutation.mutate({ key: newKey.trim(), value: newValue.trim() }, {
      onSuccess: () => { setNewKey(''); setNewValue(''); },
    });
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Global Configuration</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Platform-wide settings and parameters</p>
      </div>

      <div className="space-y-3">
        {KNOWN_KEYS.map(({ key, label, description }) => (
          <Card key={key} className="hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="font-semibold text-gray-900 dark:text-gray-100">{label}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
              </div>
              <div className="w-48">
                <Input
                  value={editValues[key] ?? configMap[key] ?? ''}
                  onChange={(e) => setEditValues({ ...editValues, [key]: e.target.value })}
                  disabled={isAuditor}
                />
              </div>
              {!isAuditor && (
                <Button size="sm" onClick={() => handleSave(key)} isLoading={savingKey === key && updateMutation.isPending}>
                  <Save className="h-4 w-4" />
                </Button>
              )}
            </div>
          </Card>
        ))}

        {configs.filter((c) => !KNOWN_KEYS.some((k) => k.key === c.key)).map((config) => (
          <Card key={config.key} className="hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="font-semibold font-mono text-gray-900 dark:text-gray-100">{config.key}</p>
              </div>
              <div className="w-48">
                <Input
                  value={editValues[config.key] ?? config.value}
                  onChange={(e) => setEditValues({ ...editValues, [config.key]: e.target.value })}
                  disabled={isAuditor}
                />
              </div>
              {!isAuditor && (
                <Button size="sm" onClick={() => handleSave(config.key)} isLoading={savingKey === config.key && updateMutation.isPending}>
                  <Save className="h-4 w-4" />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {!isAuditor && (
        <Card className="mt-8">
          <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-4">Add Custom Config</h3>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Input label="Key" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="config_key" />
            </div>
            <div className="flex-1">
              <Input label="Value" value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="value" />
            </div>
            <Button onClick={handleAddNew} isLoading={savingKey === '__new__' && updateMutation.isPending}>
              <Plus className="h-4 w-4 mr-2" /> Add
            </Button>
          </div>
        </Card>
      )}
    </AdminLayout>
  );
};
